import { ChangeEvent } from 'react';
import {
	ErrorChip,
	H4,
	ManualCarrierCost,
	NumberField,
	PriceAcceptance,
	Spacer,
	Text,
} from '@components';
import { SHIPMENT_EDIT_WINDOW, TCargoForm } from '@types';
import styles from './CargoFormContent.module.scss';

export interface CargoFormContentProps {
	form: TCargoForm;
	isFLTorPallets: boolean;
	isLoose: boolean;
	isOther: boolean;
	isItem: boolean;
	updateForm: (e: ChangeEvent<HTMLInputElement>) => void;
	updateQty: (e: ChangeEvent<HTMLInputElement>) => void;
	recalculated: boolean;
	newShipperPrice: number;
	generalError?: string | null;
	connect?: (field: string) => Record<string, any>;
	onFieldFocus?: (field: string) => void;
	isAllocated?: boolean;
	newCarrierCost?: number;
	newCarrierManualCost?: number;
	oldShipperPrice?: number;
	oldCarrierCost?: number;
	editWindow?: SHIPMENT_EDIT_WINDOW;
	disabled?: boolean;
}

export const CargoFormContent = (props: CargoFormContentProps) => {
	const {
		form,
		isFLTorPallets,
		isLoose,
		isOther,
		isItem,
		updateForm,
		updateQty,
		recalculated,
		newShipperPrice,
		generalError,
		connect,
		onFieldFocus,
		isAllocated,
		newCarrierCost = 0,
		newCarrierManualCost = 0,
		oldShipperPrice = 0,
		oldCarrierCost = 0,
		editWindow,
		disabled = false,
	} = props;

	return (
		<div className={styles.formSection}>
			<H4>Update Cargo Details</H4>
			<Spacer size={8} />
			<Text>Enter the actual quantities below. The system will automatically calculate the new price and cost based on your input.</Text>
			<Spacer size={16} />
			{generalError && (
				<>
					<ErrorChip text={generalError} stretch />
					<Spacer size={16} />
				</>
			)}
			<div className={styles.formRow}>
				{isFLTorPallets && (
					<>
						<NumberField
							label='Pallet QTY'
							name='pallet_quantities'
							value={form.pallet_quantities ?? ''}
							onChange={updateQty}
							keepErrorSpace={false}
							stretch
							disabled={disabled}
							{...(connect?.('pallet_quantities') ?? {})}
							onFocus={() => onFieldFocus?.('pallet_quantities')}
						/>
						<NumberField
							label='Pallet Spaces'
							name='pallet_spaces'
							value={form.pallet_spaces ?? ''}
							onChange={updateForm}
							keepErrorSpace={false}
							stretch
							disabled={disabled}
							{...(connect?.('pallet_spaces') ?? {})}
							onFocus={() => onFieldFocus?.('pallet_spaces')}
						/>
						<NumberField
							label='Total Weight'
							name='total_weight'
							value={form.total_weight ?? ''}
							onChange={updateForm}
							keepErrorSpace={false}
							stretch
							disabled={disabled}
							rightModule={<span className={styles.suffix}><Text color='var(--neutral-06)'>kg</Text></span>}
							{...(connect?.('total_weight') ?? {})}
							onFocus={() => onFieldFocus?.('total_weight')}
						/>
					</>
				)}
				{isLoose && (
					<NumberField
						label='Total Weight'
						name='total_weight'
						value={form.total_weight ?? ''}
						onChange={updateForm}
						keepErrorSpace={false}
						stretch
						disabled={disabled}
						rightModule={<span className={styles.suffix}><Text color='var(--neutral-06)'>kg</Text></span>}
						{...(connect?.('total_weight') ?? {})}
						onFocus={() => onFieldFocus?.('total_weight')}
					/>
				)}
				{isItem && (
					<>
						<NumberField
							label='Quantity'
							name='pallet_quantities'
							value={form.pallet_quantities ?? ''}
							onChange={updateQty}
							keepErrorSpace={false}
							stretch
							disabled={disabled}
							{...(connect?.('pallet_quantities') ?? {})}
							onFocus={() => onFieldFocus?.('pallet_quantities')}
						/>
						<NumberField
							label='Total Weight'
							name='total_weight'
							value={form.total_weight ?? ''}
							onChange={updateForm}
							keepErrorSpace={false}
							stretch
							disabled
							rightModule={<span className={styles.suffix}><Text color='var(--neutral-06)'>kg</Text></span>}
							{...(connect?.('total_weight') ?? {})}
							onFocus={() => onFieldFocus?.('total_weight')}
						/>
					</>
				)}
				{isOther && (
					<>
						<NumberField
							label='Total Weight'
							name='total_weight'
							value={form.total_weight ?? ''}
							onChange={updateForm}
							keepErrorSpace={false}
							stretch
							disabled={disabled}
							rightModule={<span className={styles.suffix}><Text color='var(--neutral-06)'>kg</Text></span>}
							{...(connect?.('total_weight') ?? {})}
							onFocus={() => onFieldFocus?.('total_weight')}
						/>
						<NumberField
							label='Quantity'
							name='pallet_spaces'
							value={form.pallet_spaces ?? ''}
							onChange={updateForm}
							keepErrorSpace={false}
							stretch
							disabled={disabled}
							{...(connect?.('pallet_spaces') ?? {})}
							onFocus={() => onFieldFocus?.('pallet_spaces')}
						/>
					</>
				)}
			</div>
			{isOther && (
				<div className={styles.formRow}>
					<NumberField
						label='Length (cm)'
						name='length'
						value={form.length ?? ''}
						onChange={updateForm}
						keepErrorSpace={false}
						stretch
						disabled={disabled}
						{...(connect?.('length') ?? {})}
						onFocus={() => onFieldFocus?.('length')}
					/>
					<NumberField
						label='Width (cm)'
						name='width'
						value={form.width ?? ''}
						onChange={updateForm}
						keepErrorSpace={false}
						stretch
						disabled={disabled}
						{...(connect?.('width') ?? {})}
						onFocus={() => onFieldFocus?.('width')}
					/>
					<NumberField
						label='Height (cm)'
						name='height'
						value={form.height ?? ''}
						onChange={updateForm}
						keepErrorSpace={false}
						stretch
						disabled={disabled}
						{...(connect?.('height') ?? {})}
						onFocus={() => onFieldFocus?.('height')}
					/>
				</div>
			)}
			<PriceAcceptance
				recalculated={recalculated}
				newShipperPrice={newShipperPrice}
				isAllocated={isAllocated}
				newCarrierCost={newCarrierCost}
				oldShipperPrice={oldShipperPrice}
				oldCarrierCost={oldCarrierCost}
				editWindow={editWindow}
			/>
			{recalculated && isAllocated && newCarrierCost === 0 && (
				<ManualCarrierCost
					useUserInputCost={form.use_user_input_cost}
					userManuallyCost={form.user_manually_cost}
					userManuallyFuelLevy={form.user_manually_fuel_levy}
					useGlobalFuelLevy={form.use_global_fuel_levy}
					updateForm={updateForm}
					newCarrierManualCost={newCarrierManualCost}
					connect={connect}
					onFieldFocus={onFieldFocus}
					disabled={disabled}
				/>
			)}
		</div>
	);
};
